import { useState, type FormEvent } from "react";
import { Button } from "@shared/components/Button";
import { Select } from "@shared/components/Select";
import { calculateBazi } from "./bazi.api";
import { FiveStructuresSection } from "./FiveStructuresSection";
import { PillarsTable } from "./PillarsTable";
import { TenGodBaseSection, TenGodTotalSection } from "./TenGodSection";
import type { BaziChart } from "./types";

const FIRST_YEAR = 1924; // the span of the solar-term table behind the luck pillars
const LAST_YEAR = 2031;

const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
const asOptions = (values: number[]) => values.map((v) => ({ value: String(v), label: String(v) }));

const YEARS = asOptions(range(FIRST_YEAR, LAST_YEAR));
const MONTHS = asOptions(range(1, 12));
const DAYS = asOptions(range(1, 31));
const HOURS = [
  "23:00 - 23:59", "00:00 - 00:59", "01:00 - 02:59", "03:00 - 04:59", "05:00 - 06:59", "07:00 - 08:59",
  "09:00 - 10:59", "11:00 - 12:59", "13:00 - 14:59", "15:00 - 16:59", "17:00 - 18:59", "19:00 - 20:59",
  "21:00 - 22:59",
].map((label, index) => ({ value: String(index + 1), label }));
const GENDERS = [
  { value: "1", label: "Male" },
  { value: "2", label: "Female" },
];

const BASES = ["year", "month", "day", "hour"] as const;

/** Public page (no sign-in): enter a birth and read the full BaZi chart, as the original discovery-dev calculator did. */
export function BaziCalculatorPage() {
  const [year, setYear] = useState("1990");
  const [month, setMonth] = useState("1");
  const [day, setDay] = useState("1");
  const [hour, setHour] = useState("1");
  const [gender, setGender] = useState("1");
  const [chart, setChart] = useState<BaziChart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setChart(
        await calculateBazi({
          year: Number(year),
          month: Number(month),
          day: Number(day),
          hour: Number(hour),
          gender: Number(gender) as 1 | 2,
        }),
      );
    } catch (err) {
      setChart(null);
      setError(err instanceof Error ? err.message : "Perhitungan gagal. Coba lagi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="bazi-page">
      <h1>BaZi Calculator</h1>

      <form className="bazi-form" onSubmit={submit}>
        <Select label="Year" options={YEARS} value={year} onChange={(e) => setYear(e.target.value)} />
        <Select label="Month" options={MONTHS} value={month} onChange={(e) => setMonth(e.target.value)} />
        <Select label="Day" options={DAYS} value={day} onChange={(e) => setDay(e.target.value)} />
        <Select label="Hour" options={HOURS} value={hour} onChange={(e) => setHour(e.target.value)} />
        <Select label="Gender" options={GENDERS} value={gender} onChange={(e) => setGender(e.target.value)} />
        <Button type="submit" disabled={busy}>
          {busy ? "Menghitung…" : "Calculate"}
        </Button>
      </form>

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}

      {chart && (
        <div className="bazi-result">
          <PillarsTable chart={chart} />
          <FiveStructuresSection data={chart.fiveStructures} />

          <h3>Ten God</h3>
          {BASES.map((base) => (
            <TenGodBaseSection
              key={base}
              base={base}
              branchName={chart[`${base}Pillar` as const].earthlyBranch.name}
              data={chart[`${base}TenGodStructures` as const]}
            />
          ))}
          <TenGodTotalSection data={chart.tenGodStructures} />

          <h3>Raw Data</h3>
          <pre className="bazi-raw" data-testid="raw-result">
            {JSON.stringify(chart, undefined, 2)}
          </pre>
        </div>
      )}
    </main>
  );
}
